/**
 * Motion primitives for the Metric site.
 *
 * The original site animates with springs described as {duration, bounce}.
 * These helpers rebuild that exact curve as a normalised easing function so a
 * tween of the same length lands on the same position at the same moment.
 *
 * bounce 0   -> critically damped, long settled tail (page load choreography)
 * bounce 0.1 -> barely underdamped, a touch snappier (scroll reveals)
 */

const DECAY = 6

const buildSpring = (bounce: number) => {
    const zeta = Math.min(Math.max(1 - bounce, 0.05), 1)
    const omega = DECAY / zeta

    const raw = (t: number): number => {
        if (t <= 0) return 0

        if (zeta >= 1) {
            return 1 - (1 + omega * t) * Math.exp(-omega * t)
        }

        const damped = omega * Math.sqrt(1 - zeta * zeta)

        return 1 - Math.exp(-zeta * omega * t) * (Math.cos(damped * t) + ((zeta * omega) / damped) * Math.sin(damped * t))
    }

    const settled = raw(1) || 1

    return (t: number): number => (t >= 1 ? 1 : raw(t) / settled)
}

/** Slow, fully settled spring used by every page-load element. */
export const easeLoad = buildSpring(0)

/** Short spring with a hint of overshoot used by every scroll reveal. */
export const easeReveal = buildSpring(0.1)

/** Interface easing for hovers, menus and accordions. */
export const easeOut = 'cubic-bezier(0.16, 1, 0.3, 1)'

export const DURATION = {
    /** Hero frame, navbar, rails, hero media. */
    frame: 2.3,
    /** Hero copy stack. */
    copy: 2.5,
    /** Scroll reveals. */
    reveal: 0.6,
    /** Grid items that only fade. */
    grid: 1.1,
    /** Small UI moves. */
    ui: 0.45
} as const

export const prefersReducedMotion = (): boolean => {
    if (typeof window === 'undefined') return false
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}
